import React, { useState } from 'react';
import { ForumPoll as ForumPollType, ForumPollOption } from '../../types/forum';
import { forumService } from '../../services/forumService';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import '../../styles/pages/ForumTopicRefactored.css';

interface ForumPollProps {
    poll: ForumPollType;
    options: ForumPollOption[];
    userVote?: string; // Option ID if user has voted
    onVote?: (optionId: string) => void;
}

const ForumPoll: React.FC<ForumPollProps> = ({ poll, options, userVote: initialUserVote, onVote }) => {
    const { user } = useAuth();
    const { t } = useLanguage();
    const [selectedOption, setSelectedOption] = useState<string | null>(null);
    const [isVoting, setIsVoting] = useState(false);
    const [userVote, setUserVote] = useState<string | undefined>(initialUserVote);
    const [currentOptions, setCurrentOptions] = useState<ForumPollOption[]>(options);
    const [animateBars, setAnimateBars] = useState(false);

    const totalVotes = currentOptions.reduce((sum, opt) => sum + opt.vote_count, 0);

    // Trigger animation on mount
    React.useEffect(() => {
        const timer = setTimeout(() => setAnimateBars(true), 100);
        return () => clearTimeout(timer);
    }, []);

    const handleVote = async () => {
        if (!selectedOption || !user) return;

        setIsVoting(true);
        try {
            await forumService.voteOnPoll(poll.id, selectedOption);

            // Update local state
            setUserVote(selectedOption);
            setCurrentOptions(prev => prev.map(opt => {
                if (opt.id === selectedOption) {
                    return { ...opt, vote_count: opt.vote_count + 1 };
                }
                return opt;
            }));

            if (onVote) onVote(selectedOption);
        } catch (error) {
            console.error('Failed to vote:', error);
            // Could show toast error here
        } finally {
            setIsVoting(false);
        }
    };

    const calculatePercentage = (votes: number) => {
        if (totalVotes === 0) return 0;
        return Math.round((votes / totalVotes) * 100);
    };

    const hasVoted = !!userVote;

    return (
        <div className="forum-poll-container">
            <div className="poll-header">
                <h3 className="poll-question">{poll.question}</h3>
                <div className="poll-meta">
                    <span className="poll-total-votes"><i className="bi bi-bar-chart-fill me-1"></i> {totalVotes} {t('forums.poll.votes')}</span>
                    {hasVoted && <span className="poll-status-badge voted"><i className="bi bi-check2-circle me-1"></i> {t('forums.poll.voted')}</span>}
                </div>
            </div>

            <div className="poll-options">
                {currentOptions.map((option) => {
                    const percentage = calculatePercentage(option.vote_count);
                    const isSelected = selectedOption === option.id;
                    const isVotedOption = userVote === option.id;

                    // Determine if this is the top voted option
                    const isTopVoted = totalVotes > 0 && option.vote_count === Math.max(...currentOptions.map(o => o.vote_count)) && option.vote_count > 0;

                    return (
                        <div
                            key={option.id}
                            className={`poll-option result-mode ${isSelected ? 'selected' : ''} ${isVotedOption ? 'voted' : ''} ${isTopVoted ? 'top-voted' : ''}`}
                        >
                            {/* Enhanced results with prominent percentages */}
                            <div className="poll-result-bar-container">
                                <div
                                    className="poll-result-bar"
                                    style={{ width: animateBars ? `${percentage}%` : '0%' }}
                                ></div>
                                <div className="poll-result-content">
                                    <div className="poll-result-text">
                                        <span className="poll-option-text">
                                            {option.option_text}
                                            {isVotedOption && <i className="bi bi-check-circle-fill voted-icon"></i>}
                                            {isTopVoted && <span className="top-badge"><i className="bi bi-trophy-fill"></i></span>}
                                        </span>
                                        <span className="poll-vote-count">{option.vote_count} {option.vote_count === 1 ? t('forums.poll.vote') : t('forums.poll.votes')}</span>
                                    </div>
                                    <div className="poll-result-percentage">
                                        {percentage}%
                                    </div>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>

            {!hasVoted && user && (
                <div className="poll-vote-section">
                    <p className="poll-vote-prompt">{t('forums.poll.castVote')}</p>
                    <div className="poll-vote-options">
                        {currentOptions.map((option) => {
                            const isSelected = selectedOption === option.id;
                            return (
                                <div
                                    key={option.id}
                                    className={`poll-vote-option ${isSelected ? 'selected' : ''}`}
                                    onClick={() => setSelectedOption(option.id)}
                                >
                                    <div className="radio-circle">
                                        {isSelected && <div className="radio-inner"></div>}
                                    </div>
                                    <span className="poll-option-text">{option.option_text}</span>
                                </div>
                            );
                        })}
                    </div>
                    <div className="poll-actions">
                        <button
                            className="poll-submit-btn"
                            disabled={!selectedOption || isVoting}
                            onClick={handleVote}
                        >
                            {isVoting ? t('forums.poll.submitting') : t('forums.poll.submit')}
                        </button>
                    </div>
                </div>
            )}

            {!user && !hasVoted && (
                <div className="poll-login-prompt">
                    <i className="bi bi-lock-fill me-2"></i>
                    {t('forums.poll.loginRequired')}
                </div>
            )}
        </div>
    );
};

export default ForumPoll;
